import React, { Suspense, lazy } from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/i18n/LanguageContext";

// Components (always loaded)
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import PageTransition from "@/components/PageTransition";
import BackToTop from "@/components/BackToTop";
import CookieBanner from "@/components/CookieBanner";

// Lazy loaded pages for better performance
const HomePage = lazy(() => import("@/pages/HomePage"));
const OSPage = lazy(() => import("@/pages/OSPage"));
const MethodologyPage = lazy(() => import("@/pages/MethodologyPage"));
const IndustriesPage = lazy(() => import("@/pages/IndustriesPage"));
const PartnersPage = lazy(() => import("@/pages/PartnersPage"));
const CompanyPage = lazy(() => import("@/pages/CompanyPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const CaseStudiesPage = lazy(() => import("@/pages/CaseStudiesPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const AdminLeadsPage = lazy(() => import("@/pages/AdminLeadsPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const CookieNoticePage = lazy(() => import("@/pages/CookieNoticePage"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-[#2ec4aa] border-t-transparent rounded-full animate-spin" aria-label="Loading"></div>
  </div>
);

function App() {
  return (
    <LanguageProvider>
      <div className="App min-h-screen bg-[#0a0a0b]">
        <BrowserRouter>
          <ScrollToTop />
          <Header />
          <main>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<PageTransition><HomePage /></PageTransition>} />
                <Route path="/vlagen-os" element={<PageTransition><OSPage /></PageTransition>} />
                <Route path="/architecture" element={<PageTransition><MethodologyPage /></PageTransition>} />
                <Route path="/solutions" element={<PageTransition><IndustriesPage /></PageTransition>} />
                <Route path="/partners" element={<PageTransition><PartnersPage /></PageTransition>} />
                <Route path="/company" element={<PageTransition><CompanyPage /></PageTransition>} />
                <Route path="/contact" element={<PageTransition><ContactPage /></PageTransition>} />
                <Route path="/cases" element={<PageTransition><CaseStudiesPage /></PageTransition>} />
                <Route path="/blog" element={<PageTransition><BlogPage /></PageTransition>} />
                <Route path="/blog/:slug" element={<PageTransition><BlogPostPage /></PageTransition>} />
                <Route path="/admin/leads" element={<PageTransition><AdminLeadsPage /></PageTransition>} />
                <Route path="/privacy-policy" element={<PageTransition><PrivacyPolicyPage /></PageTransition>} />
                <Route path="/:lang/privacy-policy" element={<PageTransition><PrivacyPolicyPage /></PageTransition>} />
                <Route path="/cookie-notice" element={<PageTransition><CookieNoticePage /></PageTransition>} />
                <Route path="/:lang/cookie-notice" element={<PageTransition><CookieNoticePage /></PageTransition>} />
                <Route path="*" element={<PageTransition><NotFoundPage /></PageTransition>} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
          <BackToTop />
          <CookieBanner />
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
